/**
 * One time zone as a picker shows it, Google Meet style:
 * "(GMT-05:00) Eastern Time - New York". The offset is the zone's standard
 * one (the smaller of January's and July's, so daylight time never moves
 * it). Used by scripts/genTimeZones.mjs at build time, and at run time for
 * a device zone the generated list does not have.
 */
export interface TimeZoneOption { id: string; label: string; offsetMinutes: number }

const YEAR = 2026;

/**
 * Zones Intl still names by an older IANA id. The id stays Intl's, which
 * every engine accepts; the label shows the current name ("Kolkata"), and
 * a device reporting the current id finds the same entry.
 */
export const CURRENT_NAMES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata', 'Asia/Katmandu': 'Asia/Kathmandu', 'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Rangoon': 'Asia/Yangon', 'Europe/Kiev': 'Europe/Kyiv', 'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca', 'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Jujuy': 'America/Argentina/Jujuy', 'America/Mendoza': 'America/Argentina/Mendoza',
  'Atlantic/Faeroe': 'Atlantic/Faroe', 'Pacific/Enderbury': 'Pacific/Kanton', 'America/Godthab': 'America/Nuuk',
  'Pacific/Truk': 'Pacific/Chuuk', 'Pacific/Ponape': 'Pacific/Pohnpei', 'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Louisville': 'America/Kentucky/Louisville', 'Africa/Asmera': 'Africa/Asmara', 'America/Coral_Harbour': 'America/Atikokan',
};

function offsetOn(id: string, date: Date): number {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: id, timeZoneName: 'longOffset' })
    .formatToParts(date).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const m = /^GMT([+-])(\d{2}):(\d{2})$/.exec(name);
  return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
}

function nameOf(id: string, style: 'longGeneric' | 'long', date: Date): string | null {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: id, timeZoneName: style })
    .formatToParts(date).find((p) => p.type === 'timeZoneName')?.value ?? '';
  return name && !name.startsWith('GMT') ? name : null;
}

/** "GMT-05:00" for -300 minutes. */
export function offsetLabel(minutes: number): string {
  const a = Math.abs(minutes);
  return `GMT${minutes < 0 ? '-' : '+'}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
}

export function zoneOption(id: string): TimeZoneOption {
  const jan = new Date(Date.UTC(YEAR, 0, 15, 12));
  const jul = new Date(Date.UTC(YEAR, 6, 15, 12));
  const [a, b] = [offsetOn(id, jan), offsetOn(id, jul)];
  const offsetMinutes = Math.min(a, b);
  const standard = a <= b ? jan : jul;
  const name = nameOf(id, 'longGeneric', standard) ?? nameOf(id, 'long', standard);
  // The city is the id's last part; none for "UTC" or the Etc zones.
  const named = CURRENT_NAMES[id] ?? id;
  const city = named.includes('/') && !named.startsWith('Etc/')
    ? named.slice(named.lastIndexOf('/') + 1).replace(/_/g, ' ') : null;
  const words = [name, city].filter(Boolean).join(' - ') || id;
  return { id, label: `(${offsetLabel(offsetMinutes)}) ${words}`, offsetMinutes };
}
